import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import {
  action,
  ConfigService,
  errorBoundary,
  withInject,
} from '@piwikpro/platform';
import { ListPreference, ListSidebarPreference } from '@piwikpro/user-preferences';
import TitleComponent from '@piwikpro/title-crate/Title';
import {
  Tabs,
  Spinner,
  Button,
  SplashScreen,
  View,
  Stack,
  Information,
  withTranslation,
} from '@piwikpro/ui-components';
import { Outlet, useLocation, useParams } from '@tanstack/react-router';
import { RouterService } from '@piwikpro/router-crate';
import { MetaSitesState } from '@piwikpro/meta-sites-crate/reducers';
import MetaSiteDetailsOverlay from './MetaSiteDetailsOverlay';

const ASCENDING = '';
const DESCENDING = '-';
const DeleteButton = action({ role: 'OWNER' })(Button);
const AddAppsButton = action({ role: 'OWNER' })(Button);

const mapStateToProps = (state: MetaSitesState) => ({
  metaSite: state.metaSites.details.settings.settings,
  isLoading: state.metaSites.details.settings.isLoading,
  failed: state.metaSites.details.settings.failed,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  config: ConfigService
  userPreferences: any
  metaSites: any
  Title: typeof TitleComponent
  routerService: RouterService
}

interface Props extends ReduxProps, InjectedProps {
  metaSiteId: string
  pathname: string
  t: TFunction
}

class MetaSiteDetails extends React.Component<Props> {
  private listSidebarPreference: any;

  constructor(props: Props) {
    super(props);

    const { config, userPreferences } = this.props;

    this.listSidebarPreference = new ListSidebarPreference(
      userPreferences.load(),
      '/administration/meta-sites',
      {
        sortBy: config.get('USER_GROUP_DEFAULT_SORT'),
      },
    );

    this.deleteMetaSite = this.deleteMetaSite.bind(this);
    this.moveToAddAppsToMetaSiteView = this.moveToAddAppsToMetaSiteView.bind(this);
    this.moveToRenameView = this.moveToRenameView.bind(this);
  }

  async componentDidMount() {
    const { metaSites, metaSiteId } = this.props;

    if (metaSiteId) {
      try {
        await metaSites.fetchMetaSiteSettings(metaSiteId);
        await metaSites.fetchMetaSiteIntegrity(metaSiteId);
        // eslint-disable-next-line no-empty
      } catch (err) {}
    }
  }

  async componentDidUpdate(prevProps) {
    const { metaSites, metaSiteId } = this.props;

    if (metaSiteId && metaSiteId !== prevProps.metaSiteId) {
      try {
        await metaSites.fetchMetaSiteSettings(metaSiteId);
        await metaSites.fetchMetaSiteIntegrity(metaSiteId);
        // eslint-disable-next-line no-empty
      } catch (err) {}
    }
  }

  async deleteMetaSite() {
    const {
      metaSites,
      metaSite,
      routerService,
    } = this.props;

    try {
      await metaSites.deleteMetaSite(metaSite.id, metaSite.attributes.name);
      await metaSites.fetchMetaSites({
        sort: {
          by: this.listSidebarPreference.sortBy,
          direction: this.listSidebarPreference.sortBy === 'name' ? ASCENDING : DESCENDING,
        },
        search: '',
      });
      routerService.push({ to: '/administration/meta-sites' });
    /* eslint-disable-next-line */
    } catch(err) {}
  }

  moveToAddAppsToMetaSiteView() {
    const { routerService } = this.props;

    routerService.openAdministrationOverlay('/o/add-apps');
  }

  moveToRenameView() {
    const { routerService } = this.props;

    routerService.openAdministrationOverlay('/m/rename');
  }

  render() {
    const {
      t,
      metaSite,
      metaSiteId,
      isLoading,
      failed,
      Title,
      routerService,
      pathname,
      userPreferences,
    } = this.props;

    if (isLoading) {
      return (
        <Spinner type="centered" className="x-large" />
      );
    }

    if (failed) {
      return (
        <SplashScreen type="notFound" />
      );
    }

    const listPreferences = new ListPreference(userPreferences.load(), '');

    const tabs = [
      {
        id: 'settings',
        label: t('meta-sites.details.settings.title'),
        value: `/administration/meta-sites/${metaSiteId}/settings/1/${listPreferences.limit}`,
      },
      {
        id: 'permissions',
        label: t('meta-sites.details.permissions.title'),
        value: `/administration/meta-sites/${metaSiteId}/permissions/users/1/${listPreferences.limit}`,
      },
    ];

    return (
      <Title part={metaSite.attributes.name} important>
        <View>
          <View.Header title={metaSite.attributes.name} caption={t('meta-sites.details.subTitle', { id: metaSite.id })}>
            <AddAppsButton appearance="primary" text={t('meta-sites.actions.add-websites')} onClick={this.moveToAddAppsToMetaSiteView} />
            <Button appearance="default" text={t('meta-sites.actions.rename-meta-site')} onClick={this.moveToRenameView} />
            <DeleteButton appearance="default" text={t('meta-sites.actions.delete-meta-site')} onClick={this.deleteMetaSite} />
          </View.Header>
          <View.Body>
            {metaSite.attributes.cnil
              && (
                <Stack fullWidth spacing="none">
                  <Stack.Item fill>
                    <div style={{ paddingBottom: '19px', width: '100%' }}>
                      <Information type="warning">{t('meta-sites.details.cnil-warning')}</Information>
                    </div>
                  </Stack.Item>
                </Stack>
              )
            }
            <Stack vertical horizontalAlignment="fill" spacing="wide">
              <Stack.Item>
                <Tabs
                  tabs={tabs}
                  RouterLink={null}
                  active={pathname.indexOf('/permissions') !== -1 ? tabs[1].value : tabs[0].value}
                  setActive={(tab: string) => {
                    if (tab.includes('/permissions')) {
                      routerService.push({
                        to: '/administration/meta-sites/$metaSiteId/permissions/users/$page/$limit',
                        params: {
                          metaSiteId,
                          page: '1',
                          limit: String(listPreferences.limit),
                        },
                      });
                      return;
                    }
                    routerService.push({
                      to: '/administration/meta-sites/$metaSiteId/settings/$page/$limit',
                      params: {
                        metaSiteId,
                        page: '1',
                        limit: String(listPreferences.limit),
                      },
                    });
                  }}
                />
              </Stack.Item>
              <Stack.Item>
                <Outlet />
              </Stack.Item>
            </Stack>
            <MetaSiteDetailsOverlay appsInsidePaginationLimit={listPreferences.limit} />
          </View.Body>
        </View>
      </Title>
    );
  }
}

const MetaSiteDetailsConnected = connector(withInject<InjectedProps>({
  config: 'config',
  userPreferences: 'userPreferences',
  metaSites: 'MetaSitesCrate.metaSites',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(withTranslation(['administration'])(
  errorBoundary(MetaSiteDetails),
)));

const MetaSiteDetailsWrapper = () => {
  const params = useParams({ from: '/_secured/administration/meta-sites/$metaSiteId' });
  const location = useLocation();

  return (
    <MetaSiteDetailsConnected
      metaSiteId={params.metaSiteId}
      pathname={location.pathname}
    />
  );
};

export default MetaSiteDetailsWrapper;
